import { accordionWidget } from './accordion';
import { adjustableContactFormWidget } from './adjustable-contact-form';
import { articleCardWidget } from './article-card';
import { bookAppointmentWidget } from './book-appointment';
import { bookAppointmentModalWidget } from './book-appointment-modal';
import { bookProjectFormWidget } from './book-project-form';
import { bulletListWidget } from './bullet-list';
import { bundeslandLocationsListWidget } from './bundesland-locations-list';
import { cardExpandableWidget } from './card-expandable';
import { cardSectionWidget } from './card-section';
import { collapsibleBlockWidget } from './collapsible-block';
import { collapsibleFineprintWidget } from './collapsible-fineprint-widget';
import { collapsibleWidget } from './collapsible-widget';
import { compactHeadlineWidget } from './compact-headline';
import { compactPricingWidget } from './compact-pricing';
import { compactRatingWidget } from './compact-rating';
import { contactWidget } from './contact-widget';
import { imageWidget } from './contentBlocks/image';
import { richTextBlockWidget } from './contentBlocks/rich-text';
import { singleTextWidget } from './contentBlocks/single-text';
import { ctaBlockWidget } from './cta-block';
import { flexibleCarouselWidget } from './flexible-carousel';
import { flexibleContentGridWidget } from './flexible-content-grid';
import { gradientSectionWidget } from './gradient-section';
import { heroWidget } from './hero';
import { horizontalDividerWidget } from './horizontal-divider';
import { htmlSnippetWidget } from './html-snippet';
import { iconCardWidget } from './icon-card-widget';
import { jobLocationsInCityWidget } from './job-locations-in-city';
import { threeColumnLayoutWidget } from './layouts/three-column-layout';
import { twoColumnLayoutWidget } from './layouts/two-column-layout';
import { listOfLocationsInCityWidget } from './list-of-locations-in-city';
import { locationFeaturesWidget } from './location-features';
import { locationFinderWidget } from './location-finder';
import { locationMapWidget } from './location-map';
import { locationSearchResultsWidget } from './location-search-results';
import { magazineArticleContentWidget } from './magazine-article-content';
import { magazineArticleListWidget } from './magazine-article-list';
import { magazineAuthorBoxWidget } from './magazine-author-box';
import { magazineAuthorPageHeaderWidget } from './magazine-author-page-header';
import { magazineTableOfContentsWidget } from './magazine-table-of-contents';
import { magazineTeaserTextWidget } from './magazine-teaser-text';
import { navigationCtasWidget } from './navigation-ctas';
import { navigationGridWidget } from './navigation-grid';
import { nearbyLocationsWidget } from './nearby-locations';
import { newsletterSubscriptionWidget } from './newsletter-subscription';
import { offerLocationsListWidget } from './offer-locations-list';
import { offerWidget } from './offer-widget';
import { pricingTeaserWidget } from './pricing-teaser';
import { ratingWidget } from './rating-widget';
import { reviewsWidget } from './reviews';
import { roomsGalleryWidget } from './rooms-gallery';
import { schoolDetailsWidget } from './school-details';
import { schoolTypesWidget } from './school-types';
import { sealsWidget } from './seals';
import { snippetReferenceWidget } from './snippet-reference';
import { subjectsWidget } from './subjects-widget';
import { teachersWidget } from './teachers-widget';
import { tickerWidget } from './ticker';
import { textContentWidget } from './title-widget';
import { tutorApplicationFormWidget } from './tutor-application-form';
import type { FrontendWidget } from './types';
import { uspRowWidget } from './usp-row';
import { videoRecommendationsWidget } from './video-recommendations-widget';
import { whatsappWidget } from './whatsapp';

// Use `any` in the generics to widen the variance — React component types
// are invariant in their props, so a registry list cannot be typed as
// `FrontendWidget<unknown, unknown>` without losing per-widget specificity.
// Per-widget call sites still get the precise type from each `index.ts` export.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyFrontendWidget = FrontendWidget<any, any>;

export const WIDGET_REGISTRY: ReadonlyArray<AnyFrontendWidget> = [
  accordionWidget,
  bookAppointmentWidget,
  tutorApplicationFormWidget,
  bookAppointmentModalWidget,
  heroWidget,
  iconCardWidget,
  locationFeaturesWidget,
  snippetReferenceWidget,
  horizontalDividerWidget,
  htmlSnippetWidget,
  offerWidget,
  sealsWidget,
  ctaBlockWidget,
  nearbyLocationsWidget,
  listOfLocationsInCityWidget,
  jobLocationsInCityWidget,
  bulletListWidget,
  bundeslandLocationsListWidget,
  schoolTypesWidget,
  tickerWidget,
  richTextBlockWidget,
  singleTextWidget,
  imageWidget,
  twoColumnLayoutWidget,
  threeColumnLayoutWidget,
  gradientSectionWidget,
  cardSectionWidget,
  textContentWidget,
  schoolDetailsWidget,
  roomsGalleryWidget,
  pricingTeaserWidget,
  compactPricingWidget,
  teachersWidget,
  subjectsWidget,
  collapsibleWidget,
  collapsibleFineprintWidget,
  videoRecommendationsWidget,
  contactWidget,
  adjustableContactFormWidget,
  ratingWidget,
  reviewsWidget,
  locationFinderWidget,
  locationMapWidget,
  locationSearchResultsWidget,
  offerLocationsListWidget,
  navigationGridWidget,
  navigationCtasWidget,
  flexibleCarouselWidget,
  flexibleContentGridWidget,
  cardExpandableWidget,
  compactRatingWidget,
  compactHeadlineWidget,
  uspRowWidget,
  whatsappWidget,
  newsletterSubscriptionWidget,
  articleCardWidget,
  magazineArticleListWidget,
  collapsibleBlockWidget,
  magazineArticleContentWidget,
  magazineAuthorPageHeaderWidget,
  magazineTeaserTextWidget,
  magazineTableOfContentsWidget,
  magazineAuthorBoxWidget,
  bookProjectFormWidget,
] as const;

export const WIDGET_BY_TYPE: ReadonlyMap<string, AnyFrontendWidget> = new Map(
  WIDGET_REGISTRY.map((w) => [w.componentType, w]),
);

export function getWidget(
  componentType: string,
): AnyFrontendWidget | undefined {
  return WIDGET_BY_TYPE.get(componentType);
}
